import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { config } from '../../../config.js';
import { getLanguageNativeLabel } from '../../../lib/language-label.js';

/**
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business ヘッダー右上の言語切替メニュー。config.supportedLanguages（ja / en / vi）を各言語の自国語表記で並べ、選択で i18next の言語を切り替える。product-innovation-lab の HeaderActions と同じ操作感
 * @graph-connects getLanguageNativeLabel [calls] 各言語の自国語表記
 */
export function LanguageMenu() {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const currentCode = i18n.language.split('-')[0] ?? config.defaultLanguage;
  const currentLanguage =
    config.supportedLanguages.find((code) => code === currentCode) ?? config.defaultLanguage;
  const currentLabel = getLanguageNativeLabel(currentLanguage);

  const handleChange = (code: string): void => {
    void i18n.changeLanguage(code);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] transition-colors text-sm font-medium"
        aria-label={t('app.language')}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
          />
        </svg>
        <span className="hidden sm:inline">{currentLabel}</span>
        <svg
          className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setIsOpen(false);
            }}
            aria-hidden="true"
          />
          <div
            role="menu"
            className="absolute right-0 mt-2 w-40 z-20 bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-xl shadow-xl py-1 animate-fade-in"
          >
            {config.supportedLanguages.map((code) => (
              <button
                key={code}
                role="menuitemradio"
                aria-checked={code === currentCode}
                lang={code}
                onClick={() => {
                  handleChange(code);
                }}
                className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                  code === currentCode
                    ? 'text-[var(--color-accent-primary)] bg-[var(--color-accent-muted)]'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]'
                }`}
              >
                {getLanguageNativeLabel(code)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
